// CSS for a slider before Swiper starts (server HTML, before hydration, JavaScript off). Two approaches the sites use:
// - `static_slider_css`: a CSS grid row standing in for the Swiper (isource / tsco-glass `components/static-slider.js`,
//   jument `components/ui/static-slider.jsx`), drawn by `StaticSlider` and swapped for the Swiper after mount.
// - `swiper_layout`: rules for Swiper's own markup until it adds `swiper-initialized`, so nothing is swapped and
//   nothing moves (mahya-tajhiz-website `components/swiper-layout.js`).
// Pure and server-safe; no imports, so it runs under plain `node --test`.

// stable short id of a config, so equal configs share one stylesheet class
export const hash_config = (value) => {
	const text = JSON.stringify(value);
	let hash = 5381;
	for (let i = 0; i < text.length; i++) hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
	return (hash >>> 0).toString(36);
};

const is_number = (value) => typeof value === "number" && Number.isFinite(value);

const sorted_widths = (breakpoints) =>
	Object.keys(breakpoints || {})
		.map(Number)
		.filter(Number.isFinite)
		.sort((a, b) => a - b);

// --- StaticSlider (grid) ---

const columns_rule = (slides_per_view, gap) =>
	is_number(slides_per_view)
		? `calc((100% - (${slides_per_view} - 1) * ${gap}px) / ${slides_per_view})`
		: "max-content"; // "auto": slides keep their own width

/** Class id of a StaticSlider config (`ss-<hash>`). */
export const static_slider_id = ({ slidesPerView, spaceBetween, breakpoints, grid } = {}) =>
	`ss-${hash_config({ slidesPerView, spaceBetween, breakpoints, grid })}`;

/** Stylesheet of a StaticSlider: a grid row with Swiper's slidesPerView, spaceBetween, breakpoints and grid rows. */
export const static_slider_css = (id, { slidesPerView, spaceBetween, breakpoints, grid } = {}) => {
	const base_gap = is_number(spaceBetween) ? spaceBetween : 0;
	const base_per_view = slidesPerView ?? 1;
	const base_rows = is_number(grid?.rows) ? grid.rows : 1;

	const sel = `.${id}`;
	let css =
		`${sel}{--ss-gap:${base_gap}px;--ss-rows:${base_rows};--ss-cols:${columns_rule(base_per_view, base_gap)};` +
		`display:grid;grid-auto-flow:column;grid-template-rows:repeat(var(--ss-rows),minmax(0,auto));` +
		`grid-auto-columns:var(--ss-cols);gap:var(--ss-gap);overflow-x:auto;overscroll-behavior-x:contain;` +
		`scroll-snap-type:x proximity;scrollbar-width:none}` +
		`${sel}::-webkit-scrollbar{display:none}` +
		`${sel}>*{min-width:0;scroll-snap-align:start}`;

	// Swiper applies a breakpoint from its width up, and later ones override earlier ones
	let gap = base_gap;
	let per_view = base_per_view;
	for (const width of sorted_widths(breakpoints)) {
		const bp = breakpoints[width] || {};
		if (is_number(bp.spaceBetween)) gap = bp.spaceBetween;
		if (bp.slidesPerView !== undefined) per_view = bp.slidesPerView;

		const rules = [`--ss-gap:${gap}px`, `--ss-cols:${columns_rule(per_view, gap)}`];
		if (is_number(bp.grid?.rows)) rules.push(`--ss-rows:${bp.grid.rows}`);

		css += `@media (min-width:${width}px){${sel}{${rules.join(";")}}}`;
	}

	return css;
};

// --- swiper_layout (Swiper's own markup) ---

// a slide's width for `slides_per_view` slides with `gap` between them, as Swiper computes it
const width_rule = (slides_per_view, gap) => {
	const count = parseFloat(slides_per_view);
	return Number.isFinite(count) && count > 0 ? `calc((100% - ${count - 1} * ${gap}px) / ${count})` : "auto";
};

/** The largest number of slides the slider shows at any width ("auto" counts as unknown). */
export const max_slides_per_view = (slidesPerView, breakpoints) => {
	const values = [slidesPerView ?? 1, ...sorted_widths(breakpoints).map((w) => breakpoints[w]?.slidesPerView)].filter(
		(value) => value !== undefined,
	);
	if (values.some((value) => value === "auto")) return "auto";
	return Math.max(...values.map((value) => parseFloat(value) || 1));
};

/**
 * Class id and CSS for a `.swiper` root until Swiper starts. Put `id` on the `.swiper` element and the css in a <style>.
 * `navigation_min_width`: arrows hidden below it; `pagination_max_width`: dots hidden from it up.
 */
export const swiper_layout = ({ slidesPerView, spaceBetween, breakpoints, navigation_min_width, pagination_max_width } = {}) => {
	const id = `sw-${hash_config({ slidesPerView, spaceBetween, breakpoints, navigation_min_width, pagination_max_width })}`;
	const root = `.${id}`;
	const before = `${root}:not(.swiper-initialized)`;

	const slide_rules = (per_view, gap) => `--ss-w:${width_rule(per_view, gap)};--ss-gap:${gap}px`;

	let gap = is_number(spaceBetween) ? spaceBetween : 0;
	let per_view = slidesPerView ?? 1;

	let css =
		`${root}{${slide_rules(per_view, gap)}}` +
		`${before}>.swiper-wrapper{overflow-x:auto;overscroll-behavior-x:contain;scrollbar-width:none;scroll-snap-type:x proximity}` +
		`${before}>.swiper-wrapper::-webkit-scrollbar{display:none}` +
		// Swiper sets the same width, and the gap as an inline margin on the reading-end side, when it starts
		`${before}>.swiper-wrapper>.swiper-slide{width:var(--ss-w);margin-inline-end:var(--ss-gap);scroll-snap-align:start}`;

	for (const width of sorted_widths(breakpoints)) {
		const bp = breakpoints[width] || {};
		if (is_number(bp.spaceBetween)) gap = bp.spaceBetween;
		if (bp.slidesPerView !== undefined) per_view = bp.slidesPerView;
		css += `@media (min-width:${width}px){${root}{${slide_rules(per_view, gap)}}}`;
	}

	if (is_number(navigation_min_width)) {
		css += `@media (max-width:${navigation_min_width - 0.02}px){${root}>.swiper-button-prev,${root}>.swiper-button-next{display:none}}`;
	}
	if (is_number(pagination_max_width)) {
		css += `@media (min-width:${pagination_max_width}px){${root}>.swiper-pagination{display:none}}`;
	}

	return { id, css };
};
